/**
 * Renders one route from the remote registry.
 *
 * The lazy component is built once per route entry and cached, not on every render:
 * React.lazy identity is what Suspense keys on, so a fresh one per render would
 * re-download the remote's chunk and remount the app on every parent re-render. The
 * registry's route objects live for the whole page, so the cache key is stable.
 */

import { lazy, useMemo, type ComponentType } from 'react';
import { loadRemote } from '@module-federation/enhanced/runtime';
import SimpleLayout from '@/components/SimpleLayout';
import { remoteModuleId, type RegistryRoute } from '@/federation/registrySchema';
import RemoteBoundary from './RemoteBoundary';

/**
 * What a remote container exposes.
 *
 * `Record<string, never>` — no props — is the truth: the shell mounts every remote bare.
 */
export type RemoteComponent = ComponentType<Record<string, never>>;

type RemoteModule = { default: RemoteComponent } | RemoteComponent;

const cache = new WeakMap<RegistryRoute, RemoteComponent>();

function normalize(mod: RemoteModule): { default: RemoteComponent } {
  // A container can expose either shape: `.default` where the exposed file is an ES
  // module, the namespace itself where it exports the component directly.
  return 'default' in mod ? (mod as { default: RemoteComponent }) : { default: mod };
}

async function load(remote: string, module: string): Promise<{ default: RemoteComponent }> {
  const id = remoteModuleId(remote, module);
  const mod = await loadRemote<RemoteModule>(id);
  // The runtime resolves null, rather than rejecting, when an error hook swallowed the
  // failure. Treated as the failure it is, so the fallback and the boundary still apply.
  if (!mod) throw new Error(`Remote module ${id} could not be loaded`);
  return normalize(mod);
}

function remoteComponentFor(route: RegistryRoute): RemoteComponent {
  const cached = cache.get(route);
  if (cached) return cached;

  const component = lazy(async () => {
    try {
      return await load(route.remote, route.module);
    } catch (error) {
      // The -detail routes fall back to their app's main page: an older deployment of the
      // app does not expose the detail module, and degrading beats dead-ending.
      if (!route.fallbackModule) throw error;
      return load(route.remote, route.fallbackModule);
    }
  });

  cache.set(route, component);
  return component;
}

export default function FederatedPage({ route }: { route: RegistryRoute }) {
  const RemoteApp = useMemo(() => remoteComponentFor(route), [route]);

  // .shell-remote (display: contents) gives the remote the system font it always had: the
  // shell's own UI is Inter, and a micro-app that sets no font of its own must not change.
  const content = (
    <div className="shell-remote">
    <RemoteBoundary remote={route.remote}>
      {route.fullHeight ? (
        // 66px is SimpleLayout's <main> padding: 50px top + 16px bottom.
        <div style={{ height: 'calc(100dvh - 66px)' }}>
          <RemoteApp />
        </div>
      ) : (
        <RemoteApp />
      )}
    </RemoteBoundary>
    </div>
  );

  if (route.bare) return content;

  return (
    <SimpleLayout title={route.title} subtitle={route.subtitle} currentPath={route.path}>
      {content}
    </SimpleLayout>
  );
}
